import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface MatchmakingSpinnerProps {
  /** Lines cycled as status text under the ring. */
  statusMessages?: string[];
  /** The big label in the center of the ring. */
  label?: string;
  /** Optional cancel handler — shows a "Cancel" button when provided. */
  onCancel?: () => void;
}

const DEFAULT_MESSAGES = [
  "Searching the design space…",
  "Shuffling the challenge deck…",
  "Consulting the design pillars…",
  "Balancing difficulty…",
  "Finalizing your challenge…",
];

/**
 * A Hearthstone "finding opponent"–style loading animation: concentric
 * rotating rings, an orbiting spark, a pulsing core, and cycling status text.
 * Purely presentational — the parent decides how long it shows.
 */
export default function MatchmakingSpinner({
  statusMessages = DEFAULT_MESSAGES,
  label = "Finding a challenge",
  onCancel,
}: MatchmakingSpinnerProps) {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex((i) => (i + 1) % statusMessages.length);
    }, 1600);
    return () => clearInterval(id);
  }, [statusMessages.length]);

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-10 select-none">
      <div className="relative h-56 w-56">
        {/* Outer glow */}
        <div className="absolute inset-0 rounded-full bg-primary/10 blur-2xl" />

        {/* Outer ring — slow spin */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-dashed border-primary/40"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        />

        {/* Middle ring — faster reverse spin, gradient arc */}
        <motion.div
          className="absolute inset-5 rounded-full border-4 border-transparent"
          style={{
            borderTopColor: "rgb(var(--primary))",
            borderRightColor: "rgb(var(--accent))",
          }}
          animate={{ rotate: -360 }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
        />

        {/* Orbiting spark */}
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
        >
          <span className="absolute left-1/2 top-1 h-3 w-3 -translate-x-1/2 rounded-full bg-accent shadow-[0_0_16px_4px_rgb(var(--accent)/0.6)]" />
        </motion.div>

        {/* Pulsing core */}
        <motion.div
          className="absolute inset-12 grid place-items-center rounded-full bg-surface ring-1 ring-border"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="font-display text-2xl font-bold text-primary">H</span>
        </motion.div>
      </div>

      <div className="flex flex-col items-center gap-2 text-center">
        <div className="font-display text-lg font-semibold text-text">
          {label}
          <AnimatedDots />
        </div>
        <motion.div
          key={msgIndex}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4 }}
          className="text-sm text-muted h-5"
        >
          {statusMessages[msgIndex]}
        </motion.div>
      </div>

      {onCancel && (
        <button
          onClick={onCancel}
          className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-muted transition-colors hover:text-text hover:bg-surface-2"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex w-6 justify-start">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: "easeInOut",
          }}
        >
          .
        </motion.span>
      ))}
    </span>
  );
}
